import { Component, ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** When this changes (e.g. the route), a caught error is cleared. */
  resetKey?: string;
}

/** After a deploy, an open tab may request code-split chunks that no longer exist. */
function isStaleChunkError(error: Error | null): boolean {
  return !!error && /dynamically imported module|Loading chunk|Importing a module script failed/i.test(error.message);
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset = () => {
    if (isStaleChunkError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-[var(--color-midnight)] mb-2">
              Something Went Wrong
            </h2>
            <p className="text-sm text-[var(--color-ink-muted)] mb-6">
              {isStaleChunkError(this.state.error)
                ? "A new version of the site is available. Reload to continue."
                : "An unexpected error occurred while rendering this page."}
            </p>
            {import.meta.env.DEV && this.state.error && (
              <p className="text-xs text-red-500 bg-red-50 rounded-lg p-3 mb-6 font-mono text-left overflow-auto max-h-24">
                {this.state.error.message}
              </p>
            )}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="btn-gold flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" /> {isStaleChunkError(this.state.error) ? "Reload" : "Try again"}
              </button>
              <Link to="/" className="btn-outline flex items-center gap-2">
                <Home className="w-4 h-4" /> Go Home
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
